"use client";

import { useEffect } from "react";
import Link from "next/link";
import posthog from "posthog-js";

interface Props {
  error: Error & { digest?: string };
  reset: () => void;
}

export default function Error({ error, reset }: Props) {
  useEffect(() => {
    // React error boundaries swallow errors before PostHog's global handler
    // sees them, so report explicitly to keep visibility into resume-time crashes.
    posthog.captureException(error);
  }, [error]);

  return (
    <main
      id="main-content"
      tabIndex={-1}
      className="relative flex min-h-[calc(100dvh-4rem)] items-center justify-center overflow-hidden px-6 outline-none"
    >
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            "radial-gradient(circle, color-mix(in srgb, var(--foreground) 3%, transparent) 1px, transparent 1px)",
          backgroundSize: "30px 30px",
        }}
      />
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 80% 70% at 50% 50%, transparent 40%, var(--background) 100%)",
        }}
      />

      <div className="relative z-10 flex max-w-md flex-col items-center text-center">
        <div
          aria-hidden="true"
          className="border-foreground/10 bg-foreground/[0.03] mb-8 flex h-16 w-16 items-center justify-center rounded-2xl border"
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M12 8v5M12 16.5v.5M10.3 3.9 2.4 18a1.9 1.9 0 0 0 1.7 2.9h15.8a1.9 1.9 0 0 0 1.7-2.9L13.7 3.9a1.9 1.9 0 0 0-3.4 0Z"
              stroke="var(--brand)"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        <h1 className="text-foreground mb-3 text-2xl font-bold tracking-tight sm:text-3xl">
          Something went wrong
        </h1>
        <p className="text-muted-foreground mb-10 max-w-[24rem] text-sm leading-relaxed">
          The page ran into an unexpected error. Try reloading it — if the problem keeps happening,
          head back to the homepage.
        </p>

        <div className="flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => reset()}
            className="inline-flex items-center gap-2 rounded-lg px-7 py-2.5 text-sm font-semibold tracking-tight transition-all duration-200 hover:-translate-y-0.5 hover:opacity-[0.82] active:translate-y-0"
            style={{ background: "var(--foreground)", color: "var(--background)" }}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <path
                d="M12 7a5 5 0 1 1-1.46-3.54M12 1.5V4h-2.5"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Try again
          </button>
          <Link
            href="/"
            className="border-foreground/12 text-foreground/80 hover:border-foreground/25 hover:text-foreground inline-flex items-center gap-2 rounded-lg border px-7 py-2.5 text-sm font-semibold tracking-tight transition-all duration-200"
          >
            Back to home
          </Link>
        </div>
      </div>
    </main>
  );
}
